import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { RM } from "@/lib/format";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const inputCls = "border-[#E2E5EA] bg-[#F4F5F7] text-[#0F172A] focus-visible:ring-red-500";

export const AttendanceDialog = ({ open, onOpenChange, item, onSaved }) => {
  const [form, setForm] = useState({ ot_normal_hours: 0, ot_rest_hours: 0, ot_holiday_hours: 0, unpaid_leave_days: 0, bonus: 0, other_deductions: 0 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item)
      setForm({
        ot_normal_hours: item.ot_normal_hours ?? item.overtime_hours ?? 0,
        ot_rest_hours: item.ot_rest_hours || 0,
        ot_holiday_hours: item.ot_holiday_hours || 0,
        unpaid_leave_days: item.unpaid_leave_days || 0,
        bonus: item.bonus || 0,
        other_deductions: item.other_deductions || 0,
      });
  }, [item]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/payroll/items/${item.id}`, {
        ot_normal_hours: parseFloat(form.ot_normal_hours) || 0,
        ot_rest_hours: parseFloat(form.ot_rest_hours) || 0,
        ot_holiday_hours: parseFloat(form.ot_holiday_hours) || 0,
        unpaid_leave_days: parseFloat(form.unpaid_leave_days) || 0,
        bonus: parseFloat(form.bonus) || 0,
        other_deductions: parseFloat(form.other_deductions) || 0,
      });
      toast.success("Attendance updated & recalculated");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md border-[#E2E5EA] bg-[#FFFFFF] text-[#0F172A]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl">
            <Clock size={20} className="text-red-500" /> Attendance · {item.employee_name}
          </DialogTitle>
          <DialogDescription className="text-[#9CA3AF]">
            OT hourly rate = gross ÷ 26 ÷ 7.5. Normal-day, rest-day and public-holiday hours use their own multipliers (set in Settings). Unpaid leave deducts the daily rate (basic ÷ 26).
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#9CA3AF]">Normal-day OT (hrs)</Label>
            <Input data-testid="att-ot-normal" type="number" value={form.ot_normal_hours} onChange={(e) => set("ot_normal_hours", e.target.value)} className={`mt-1.5 ${inputCls}`} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#9CA3AF]">Rest-day OT (hrs)</Label>
            <Input data-testid="att-ot-rest" type="number" value={form.ot_rest_hours} onChange={(e) => set("ot_rest_hours", e.target.value)} className={`mt-1.5 ${inputCls}`} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#9CA3AF]">Public-holiday OT (hrs)</Label>
            <Input data-testid="att-ot-holiday" type="number" value={form.ot_holiday_hours} onChange={(e) => set("ot_holiday_hours", e.target.value)} className={`mt-1.5 ${inputCls}`} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#9CA3AF]">Unpaid Leave (days)</Label>
            <Input data-testid="att-unpaid-days" type="number" value={form.unpaid_leave_days} onChange={(e) => set("unpaid_leave_days", e.target.value)} className={`mt-1.5 ${inputCls}`} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#9CA3AF]">Bonus (RM)</Label>
            <Input data-testid="att-bonus" type="number" value={form.bonus} onChange={(e) => set("bonus", e.target.value)} className={`mt-1.5 ${inputCls}`} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#9CA3AF]">Other Deductions (RM)</Label>
            <Input data-testid="att-other-ded" type="number" value={form.other_deductions} onChange={(e) => set("other_deductions", e.target.value)} className={`mt-1.5 ${inputCls}`} />
          </div>
        </div>
        <div className="flex items-center justify-between border-t border-[#E2E5EA] pt-3 text-sm">
          <span className="text-[#9CA3AF]">Current net pay</span>
          <span className="font-semibold tabular-nums text-red-500">{RM(item.net_pay)}</span>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#E2E5EA] bg-transparent text-[#0F172A] hover:bg-[#EEF1F5]">Cancel</Button>
          <Button data-testid="save-attendance" onClick={save} disabled={saving} className="bg-red-500 font-semibold text-white hover:bg-red-600">
            {saving ? "Saving…" : "Save & Recalculate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
